import React, { useState, useMemo, useEffect } from "react";
import { ViewBooking } from "collins-js-sdk";
import { EDSButton } from "../../../../eds/EDSButton";
import Table from "../../../../Shared/Table/Table";
import { PillsWithOverflow } from "../../Sidebar/DiaryBookingCardList/RunSheetCard/PillsWithOverflow";
import CustomPill from "../../../../Shared/CustomPill/CustomPill";
import { useBookingPermissions } from "../../../../../hooks/useBookingPermissions";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { useQuery } from "@tanstack/react-query";
import { fetchBookingV4FromAPI } from "../../../../../APIs/Booking/GetBooking";
import Loading from "../../../../../utils/Loading";
import {
  getCustomerName,
  formatTimeForLocale
} from "../../../../../utils/booking";
import {
  getBookingAssignedAreaNamesAsString,
  getBookingDurationAsString,
  getBookingEndTimeAsString,
  getBookingTimeAsString,
  getBookingType,
  getBookingsZoneNamesAsString
} from "../../../../../utils/singleBooking";
import {
  getSelectedBookingLabels,
  getSelectedCustomerLabels
} from "../../SelectedBooking/Labels/BookingLabels";
import {
  getPreOrdersData,
  getPreOrderTotals
} from "../../SelectedBooking/PreOrders/SelectedBookingPreOrdersController";
import { getPaymentsData } from "../../SelectedBooking/Payments/SelectedBookingPaymentsController";
import { VenueConfig } from "../../../../../types/venues";
import { VenueGroupConfig } from "../../../../../types/VenueGroup";
import { CalendarDays, Clock, Pencil, User } from "lucide-react";
import EditBooking from "../../SelectedBooking/EditBooking/EditBooking";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription
} from "../../../../ui/dialog";
import { filterPreOrdersData } from "./helpers";
import { InputField } from "./InputField";
import { CheckboxOption } from "./CheckboxOption";
import { CopyableBookingRef } from "../../../../Shared/CopyableBookingRef/CopyableBookingRef";
import { SectionHeader } from "./SectionHeader";
import { EmptyState } from "./EmptyState";
import {
  getPreviewModalConfigFromStorage,
  setPreviewModalConfigToStorage
} from "../../../../../utils/localStorage/previewModalConfigStorage";
import { PreviewModalConfigChangeHandler } from "../../../../../types/modalConfig";

type PreviewBookingModalProps = {
  booking: ViewBooking;
  venueConfig: VenueConfig;
  venueGroupConfig: VenueGroupConfig | null;
  open: boolean;
  onClose: () => void;
};

export const PreviewBookingModalBeta = (props: PreviewBookingModalProps) => {
  const { booking, venueConfig, venueGroupConfig, open, onClose } = props;

  const [separateByType, setSeparateByType] = useState(false);
  const [separateBySubtype, setSeparateBySubtype] = useState(false);
  const [separateFixedPriceMenuItems, setSeparateFixedPriceMenuItems] =
    useState(false);
  const [separateByTime, setSeparateByTime] = useState(false);
  const [displayForNames, setDisplayForNames] = useState(false);
  const [showEditBooking, setShowEditBooking] = useState<boolean>(false);

  useEffect(() => {
    const loadConfig = async () => {
      const config = await getPreviewModalConfigFromStorage();
      setSeparateByType(config.separateByType);
      setSeparateBySubtype(config.separateBySubtype);
      setSeparateFixedPriceMenuItems(config.separateFixedPriceMenuItems);
      setSeparateByTime(config.separateByTime);
      setDisplayForNames(config.displayForNames);
    };
    loadConfig();
  }, []);

  const handleConfigChange: PreviewModalConfigChangeHandler = async (
    key,
    value,
    setter
  ) => {
    setter(value);
    const currentConfig = await getPreviewModalConfigFromStorage();
    const updatedConfig = { ...currentConfig, [key]: value };
    await setPreviewModalConfigToStorage(updatedConfig);
  };

  const diaryContext = useDiaryContext();
  const { selectedBookingV4 } = diaryContext;
  const { userCanManageBooking } = useBookingPermissions();

  const { data: fetchedBookingV4, isLoading: isFetchingBooking } = useQuery({
    queryKey: ["getBookingForPreview", booking.id],
    queryFn: () =>
      booking.hasPreorders && !selectedBookingV4 && booking.id
        ? fetchBookingV4FromAPI(booking.id)
        : undefined,
    enabled: booking.hasPreorders && !selectedBookingV4 && !!booking.id
  });

  const bookingV4Data = selectedBookingV4 ?? fetchedBookingV4;

  const customerName = getCustomerName(booking);
  const bookingTimeAsString = formatTimeForLocale(
    getBookingTimeAsString(booking)
  );
  const bookingEndTimeAsString = formatTimeForLocale(
    getBookingEndTimeAsString(booking)
  );
  const bookingDurationAsString = getBookingDurationAsString(booking);
  const bookingAssignedAreasAsString =
    getBookingAssignedAreaNamesAsString(booking);
  const bookingsZoneNamesAsString = getBookingsZoneNamesAsString({
    selectedBooking: booking,
    venueConfig
  });
  const bookingType = getBookingType({ selectedBooking: booking, venueConfig });

  const bookingLabels = venueGroupConfig
    ? getSelectedBookingLabels({
        venueConfig,
        venueGroupConfig,
        selectedBooking: booking
      })
    : [];

  const customerLabels =
    booking.customerId && venueGroupConfig
      ? getSelectedCustomerLabels({
          venueConfig,
          venueGroupConfig,
          selectedCustomer: { id: booking.customerId }
        })
      : [];

  const allLabels = [
    ...bookingLabels.map((label) => ({
      key: `booking-${label.id}`,
      label: label.label,
      backgroundColour: `#${label.colour}`,
      softenColour: false
    })),
    ...customerLabels.map((label) => ({
      key: `customer-${label.id}`,
      label: label.label,
      backgroundColour: `#${label.colour}`,
      softenColour: false
    }))
  ];

  const currency = venueConfig?.currency ?? "GBP";
  const preOrdersData = bookingV4Data
    ? getPreOrdersData(bookingV4Data, currency)
    : [];
  const preOrderTotals = bookingV4Data
    ? getPreOrderTotals(bookingV4Data, currency)
    : [];
  const paymentsData = bookingV4Data ? getPaymentsData(bookingV4Data) : [];

  const filteredPreOrdersData = useMemo(() => {
    return filterPreOrdersData(preOrdersData, {
      separateByType,
      separateBySubtype,
      separateFixedPriceMenuItems,
      separateByTime,
      displayForNames
    });
  }, [
    preOrdersData,
    separateByType,
    separateBySubtype,
    separateFixedPriceMenuItems,
    separateByTime,
    displayForNames
  ]);

  const handleEditBooking = () => {
    setShowEditBooking(true);
  };

  if (showEditBooking) {
    return (
      <EditBooking
        booking={booking}
        venueConfig={venueConfig}
        handleClose={() => {
          setShowEditBooking(false);
          onClose();
        }}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="PreviewBookingModalBeta max-w-[calc(100%-2rem)] sm:max-w-[80vw] max-h-[90vh] overflow-hidden flex flex-col p-6 bg-[var(--panelBackground)] text-[var(--primaryColor)]">
        <DialogTitle className="sr-only">
          Booking {booking.reference} at {venueConfig?.title ?? "venue"}
        </DialogTitle>
        <DialogDescription className="sr-only">
          View booking details including customer information, pre-orders, and
          payments
        </DialogDescription>
        <div className="PreviewBookingModalBeta__header flex justify-between items-center w-full mb-4">
          <div className="PreviewBookingModalBeta__headerLeft flex flex-col gap-1">
            <div className="PreviewBookingModalBeta__venueName font-semibold text-xl text-[var(--primaryColor)] leading-tight">
              {venueConfig?.title ?? ""}
            </div>
            <div className="PreviewBookingModalBeta__bookingRef text-base text-[var(--primaryColor)] font-normal">
              Booking Ref: <CopyableBookingRef reference={booking.reference} />
            </div>
          </div>
        </div>

        <div className="PreviewBookingModalBeta__meta flex flex-wrap gap-6 mb-4 shrink-0">
          <div className="PreviewBookingModalBeta__metaItem flex items-center gap-2 text-[0.95rem] text-[var(--primaryColor)] font-normal">
            <CalendarDays className="PreviewBookingModalBeta__metaIcon w-4 h-4 flex justify-center" />
            <span>
              {booking.date
                ? new Date(booking.date).toLocaleDateString()
                : "No date"}
            </span>
          </div>
          <div className="PreviewBookingModalBeta__metaItem flex items-center gap-2 text-[0.95rem] text-[var(--primaryColor)] font-normal">
            <Clock className="PreviewBookingModalBeta__metaIcon w-4 h-4 flex justify-center" />
            <span>
              {bookingTimeAsString} - {bookingEndTimeAsString} (
              {bookingDurationAsString})
            </span>
          </div>
          {bookingAssignedAreasAsString && (
            <div className="PreviewBookingModalBeta__metaItem flex items-center gap-2 text-[0.95rem] text-[var(--primaryColor)] font-normal">
              <span>Areas: {bookingAssignedAreasAsString}</span>
            </div>
          )}
          {bookingsZoneNamesAsString && (
            <div className="PreviewBookingModalBeta__metaItem flex items-center gap-2 text-[0.95rem] text-[var(--primaryColor)] font-normal">
              <span>Zones: {bookingsZoneNamesAsString}</span>
            </div>
          )}
        </div>

        {allLabels.length > 0 && (
          <div className="PreviewBookingModalBeta__tags mb-4 shrink-0">
            <PillsWithOverflow pills={allLabels} maxHeight={80} />
          </div>
        )}

        <div className="PreviewBookingModalBeta__scrollableContent flex-1 overflow-y-auto flex flex-col gap-3 pr-6">
          <div className="PreviewBookingModalBeta__columns grid grid-cols-1 md:grid-cols-2 gap-8 mb-4">
            <div className="PreviewBookingModalBeta__column">
              <h3 className="mb-6 font-semibold text-[1.1rem] text-[var(--primaryColor)]">
                Customer Details
              </h3>
              <div className="PreviewBookingModalBeta__stackedInputs flex flex-col gap-6">
                <InputField label="Name" value={customerName} />
                {booking.company && (
                  <InputField label="Company" value={booking.company} />
                )}
                <InputField label="Phone" value={booking.phone} />
                <InputField label="Email" value={booking.email} />
              </div>
            </div>

            <div className="PreviewBookingModalBeta__column">
              <h3 className="mb-6 font-semibold text-[1.1rem] text-[var(--primaryColor)]">
                Booking Details
              </h3>
              <div className="PreviewBookingModalBeta__bookingInfo flex flex-col gap-6">
                <div className="PreviewBookingModalBeta__guests flex justify-between items-center pb-3 border-b border-[var(--borderColor)]">
                  <span className="label text-[0.95rem] text-[var(--primaryColor)] font-normal">
                    Guests
                  </span>
                  <span className="value flex items-center gap-2 text-[0.95rem] font-normal text-[var(--primaryColor)]">
                    <User className="PreviewBookingModalBeta__guestIcon w-4 h-4 text-[var(--primaryColor)]" />
                    {booking.numPeople}
                  </span>
                </div>
                {bookingType && (
                  <div className="PreviewBookingModalBeta__bookingType flex justify-between items-center pb-3 border-b border-[var(--borderColor)]">
                    <span className="label text-[0.95rem] text-[var(--primaryColor)] font-normal">
                      Booking type
                    </span>
                    <CustomPill
                      label={bookingType.name}
                      backgroundColour={bookingType.color}
                      softenColour={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="PreviewBookingModalBeta__preOrders">
            <SectionHeader title="Pre-Orders" />
            <div className="PreviewBookingModalBeta__options flex flex-wrap gap-6 mb-4 p-0">
              <CheckboxOption
                id="separateByType"
                label="Separate by type"
                checked={separateByType}
                onChange={(checked) =>
                  handleConfigChange(
                    "separateByType",
                    checked,
                    setSeparateByType
                  )
                }
              />
              <CheckboxOption
                id="separateBySubtype"
                label="Separate by subtype"
                checked={separateBySubtype}
                onChange={(checked) =>
                  handleConfigChange(
                    "separateBySubtype",
                    checked,
                    setSeparateBySubtype
                  )
                }
              />
              <CheckboxOption
                id="separateFixedPriceMenuItems"
                label="Separate fixed price menu items"
                checked={separateFixedPriceMenuItems}
                onChange={(checked) =>
                  handleConfigChange(
                    "separateFixedPriceMenuItems",
                    checked,
                    setSeparateFixedPriceMenuItems
                  )
                }
              />
              <CheckboxOption
                id="separateByTime"
                label="Separate by time"
                checked={separateByTime}
                onChange={(checked) =>
                  handleConfigChange(
                    "separateByTime",
                    checked,
                    setSeparateByTime
                  )
                }
              />
              <CheckboxOption
                id="displayForNames"
                label="Display for names"
                checked={displayForNames}
                onChange={(checked) =>
                  handleConfigChange(
                    "displayForNames",
                    checked,
                    setDisplayForNames
                  )
                }
              />
            </div>

            {isFetchingBooking && <Loading />}
            {!isFetchingBooking && filteredPreOrdersData.length > 0 && (
              <div className="PreviewBookingModalBeta__preOrdersTable">
                <Table
                  columns={[
                    {
                      dataNameJSX: "timeCol",
                      caption: "Time"
                    },
                    {
                      dataNameJSX: "itemCol",
                      caption: "Item"
                    },
                    {
                      dataName: "quantity",
                      caption: "Quantity",
                      className: "BookingPayments-TableColumnQuantity",
                      align: "Right"
                    },
                    {
                      dataNameJSX: "priceCol",
                      caption: "Price",
                      className: "BookingPayments-TableColumnMoney",
                      align: "Right"
                    },
                    {
                      dataName: "total",
                      caption: "Total",
                      className: "BookingPayments-TableColumnMoney",
                      align: "Right"
                    }
                  ]}
                  data={filteredPreOrdersData}
                />
                {preOrderTotals.length > 0 && (
                  <div className="PreviewBookingModalBeta__totals">
                    <Table
                      columns={[
                        { dataName: "description", caption: "" },
                        { dataName: "total", caption: "", align: "Right" }
                      ]}
                      data={preOrderTotals}
                    />
                  </div>
                )}
              </div>
            )}
            {!isFetchingBooking && filteredPreOrdersData.length === 0 && (
              <EmptyState message="No pre-orders for this booking" />
            )}
          </div>

          <div className="PreviewBookingModalBeta__payments">
            <SectionHeader title="Payments & Deposits" />
            {isFetchingBooking && <Loading />}
            {!isFetchingBooking && paymentsData.length > 0 && (
              <div className="PreviewBookingModalBeta__paymentsTable">
                <Table
                  columns={[
                    {
                      dataName: "displayDate",
                      caption: "Added"
                    },
                    {
                      dataName: "displayType",
                      caption: "Type"
                    },
                    {
                      dataName: "displayAmount",
                      caption: "Amount",
                      className: "BookingPayments-TableColumnAmount",
                      align: "Right"
                    },
                    {
                      dataName: "displayStatus",
                      leftIconDataName: "displayStatusIcon",
                      caption: "Status",
                      className: "BookingPayments-TableColumnStatus",
                      align: "Right"
                    }
                  ]}
                  data={paymentsData}
                />
              </div>
            )}
            {!isFetchingBooking && paymentsData.length === 0 && (
              <EmptyState message="No payments for this booking" />
            )}
          </div>
        </div>

        <div className="PreviewBookingModalBeta__footer flex justify-between items-center gap-4 pt-4 pb-6 border-t border-[var(--borderColor)] shrink-0">
          <div />
          <div className="footer-right flex gap-4">
            <EDSButton
              variant="outline"
              onClick={onClose}
              className="btn btn-cancel"
            >
              Cancel
            </EDSButton>
            {userCanManageBooking && (
              <EDSButton
                variant="default"
                onClick={handleEditBooking}
                className="btn btn-primary"
              >
                <Pencil />
                Edit Booking
              </EDSButton>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
